import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "../tattoo/lib/supabaseClient";
import { useAuth } from "./AuthProvider";

export default function ProtectedRoute({ children, allowSetup = false }) {
  const { session, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const loc = useLocation();

  const qp = new URLSearchParams(loc.search);
  const allowGuestArtistFlash =
    qp.get("guest") === "1" && qp.get("from") === "artist-flash";

  // Dev bypass — skips the auth guard entirely.
  // Enable via EITHER:
  //   - .env.local file with:  VITE_AUTH_BYPASS=1
  //   - browser console:       localStorage.setItem("tt_dev_bypass", "1")
  // Both only work when the app is running in dev mode.
  // Note: Supabase queries still require a real session, so anything that
  // hits the DB (shop profile, save/share, etc.) will fail under RLS while
  // bypassed. Use for UI work only.
  const devBypass =
    import.meta.env.DEV &&
    (import.meta.env.VITE_AUTH_BYPASS === "1" ||
      localStorage.getItem("tt_dev_bypass") === "1");
  if (devBypass) return children;

  useEffect(() => {
    let cancelled = false;
    let channel;

    (async () => {
      if (allowGuestArtistFlash) {
        if (!cancelled) {
          setChecking(false);
          setProfileLoaded(true);
          setNeedsSetup(false);
        }
        return;
      }

      if (loading) return; // wait for auth boot
      if (!session) {
        if (!cancelled) {
          setChecking(false);
          setProfileLoaded(false);
          setNeedsSetup(false);
        }
        return;
      }

      try {
        const user = session.user;

        // Initial read
        const { data, error } = await supabase
          .from("profiles")
          .select("username")
          .eq("id", user.id)
          .maybeSingle();

        if (error) throw error;

        const uname = data?.username?.trim();
        if (!cancelled) {
          setNeedsSetup(!uname);
          setProfileLoaded(true);
        }

        // Realtime: react immediately when username is set
        channel = supabase
          .channel(`profiles-self-${user.id}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "profiles",
              filter: `id=eq.${user.id}`,
            },
            (payload) => {
              const next = payload.new?.username?.trim();
              setNeedsSetup(!next);
              setProfileLoaded(true);
            }
          )
          .subscribe();
      } catch (e) {
        console.warn("[protected] profile check failed:", e);
        // Fail open to avoid trapping forever
        if (!cancelled) {
          setNeedsSetup(false);
          setProfileLoaded(true);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [session, loading, allowGuestArtistFlash]);

  // One-time optimistic pass if we *just* saved username and are navigating to /app.
  // This prevents the tiny read-after-write race from bouncing you back.
  const optimisticPass = loc.state?.usernameJustSaved === true;

  if (!allowGuestArtistFlash && (loading || checking || !profileLoaded)) {
    return <div style={{ padding: 24, color: "#ddd" }}>Loading…</div>;
  }

  if (!session && !allowGuestArtistFlash) {
    return <Navigate to="/" replace state={{ from: loc }} />;
  }

  const actuallyNeedsSetup = optimisticPass ? false : needsSetup;

  if (!allowGuestArtistFlash) {
    if (actuallyNeedsSetup && !allowSetup) {
      return <Navigate to="/setup" replace />;
    }

    if (!actuallyNeedsSetup && allowSetup) {
      return <Navigate to="/app" replace />;
    }
  }

  return children;
}